import type { SelectChangeEvent } from '@mui/material'
import {
  Box,
  Button,
  Drawer,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material'
import type { FC } from 'react'
import { useContext, useEffect, useState } from 'react'

import ctx from '@/context/AppContext'
import { useFraudStatusMutation } from '@/lib/apollo'
import { FraudStatus } from '@/lib/apollo/query/get-applicant'
import vouchLogger from '@/lib/vouchLogger'
import type { FraudDecision } from '@/types/Activity'
import { FraudStatus as ActivityFraudStatus } from '@/types/Activity'
import { Role } from '@/types/User'

import { FRAUD_DECISION_LABELS } from './labels'

interface Props {
  isOpen: boolean
  onClose: () => void
  customerId: string
  subjectId: string
  fraudStatus: FraudStatus
  fraudReason: string | null
  fraudDecision: FraudDecision | null
}

const NOT_FRAUD_SENTINEL = '__not_fraud__'

type DecisionValue = FraudDecision | typeof NOT_FRAUD_SENTINEL | ''

const FRAUD_DECISION_OPTIONS: { value: DecisionValue; label: string }[] = [
  ...Object.entries(FRAUD_DECISION_LABELS).map(([value, label]) => ({
    value: value as FraudDecision,
    label,
  })),
  { value: NOT_FRAUD_SENTINEL, label: 'Not fraud' },
]

const toFraudStatusLabel = (status: FraudStatus): string => {
  switch (status) {
    case FraudStatus.notFraudulent:
      return 'not fraud'
    case FraudStatus.confirmed:
      return 'confirmed fraud'
    case FraudStatus.potential:
      return 'suspicions of fraud'
    case FraudStatus.awaitingFraudTeamReview:
      return 'awaiting fraud team investigation'
  }
}

const outlinedButtonSx = {
  textTransform: 'none',
  fontWeight: 500,
  py: 2,
  borderRadius: '10px',
  fontSize: '13px',
  letterSpacing: '0.46px',
  borderColor: '#1B4554',
  color: '#1B4554',
  '&:hover': { borderColor: '#1B4554', bgcolor: 'rgba(27, 69, 84, 0.06)' },
} as const

const containedButtonSx = {
  textTransform: 'none',
  fontWeight: 600,
  py: 2,
  bgcolor: '#1B4554',
  '&:hover': { bgcolor: '#13323d' },
} as const

interface SidebarContentProps {
  loading: boolean
  isAwaitingReview: boolean
  isFraudTeamMember: boolean
  decision: DecisionValue
  reason: string
  error: Error | undefined
  onDecisionChange: (value: DecisionValue) => void
  onReasonChange: (value: string) => void
}

const SidebarContent: FC<SidebarContentProps> = ({
  loading,
  isAwaitingReview,
  isFraudTeamMember,
  decision,
  reason,
  error,
  onDecisionChange,
  onReasonChange,
}) => {
  if (loading) {
    return <Typography>Updating fraud status…</Typography>
  }

  if (isAwaitingReview && !isFraudTeamMember) {
    return (
      <Typography>This reference is under investigation by the fraud team.</Typography>
    )
  }

  if (isAwaitingReview && isFraudTeamMember) {
    return (
      <>
        <FormControl fullWidth>
          <InputLabel id="fraud-decision-label">Outcome category</InputLabel>
          <Select
            labelId="fraud-decision-label"
            label="Outcome category"
            value={decision}
            onChange={(e: SelectChangeEvent<DecisionValue>) =>
              onDecisionChange(e.target.value as DecisionValue)
            }
          >
            {FRAUD_DECISION_OPTIONS.map(opt => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          label="Leave a comment explaining why"
          multiline
          rows={6}
          fullWidth
          value={reason}
          onChange={e => onReasonChange(e.target.value)}
        />
        {error && (
          <Typography color="error">
            An unexpected error occurred. {error.message}
          </Typography>
        )}
      </>
    )
  }

  return (
    <>
      <TextField
        label="Leave a comment explaining why"
        multiline
        rows={6}
        fullWidth
        value={reason}
        onChange={e => onReasonChange(e.target.value)}
      />
      {error && (
        <Typography color="error">
          An unexpected error occurred. {error.message}
        </Typography>
      )}
    </>
  )
}

const FraudFlagSidebar: FC<Props> = ({
  isOpen,
  onClose,
  customerId,
  subjectId,
  fraudStatus,
  fraudReason,
  fraudDecision,
}) => {
  const [setFraudStatus, { loading, error, reset }] = useFraudStatusMutation()

  const [reason, setReason] = useState<string>('')
  const [decision, setDecision] = useState<DecisionValue>('')

  useEffect(() => {
    if (isOpen) {
      reset()
      setReason('')
      setDecision('')
    }
  }, [isOpen, reset])

  const { personId, workspaceSettings } = useContext(ctx)

  const isFraudTeamMember =
    workspaceSettings?.lastAccessedCustomer.roles &&
    workspaceSettings?.lastAccessedCustomer.roles.includes(Role.fraud)

  const isAwaitingReview = fraudStatus === FraudStatus.awaitingFraudTeamReview

  const handleFraudTeamConfirm = () => {
    const isNotFraud = decision === NOT_FRAUD_SENTINEL
    setFraudStatus({
      variables: {
        customerId,
        subjectId,
        userId: personId ?? '',
        fraudStatus: isNotFraud
          ? ActivityFraudStatus.notFraudulent
          : ActivityFraudStatus.confirmed,
        reason,
        fraudDecision:
          !isNotFraud && decision !== '' ? (decision as FraudDecision) : undefined,
      },
    })
      .then(() => onClose())
      .catch((err: Error) => {
        vouchLogger.error('Fraud team confirm failed', {
          error: err,
          subjectId,
          customerId,
        })
      })
  }

  const handleSendToFraudTeam = () => {
    setFraudStatus({
      variables: {
        customerId,
        subjectId,
        userId: personId ?? '',
        fraudStatus: ActivityFraudStatus.awaitingFraudTeamReview,
        reason,
      },
    })
      .then(() => onClose())
      .catch((err: Error) => {
        vouchLogger.error('Send to fraud team failed', {
          error: err,
          subjectId,
          customerId,
        })
      })
  }

  const canConfirmFraudTeam = reason !== '' && decision !== ''
  const canSendToFraudTeam = reason !== ''

  return (
    <Drawer
      anchor="right"
      open={isOpen}
      onClose={onClose}
      ModalProps={{
        keepMounted: true,
        disableScrollLock: true,
        BackdropProps: { invisible: true },
      }}
      PaperProps={{
        sx: {
          width: '40%',
          borderLeft: '2px solid #f2f2f2',
          boxShadow: 'none',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <Box sx={{ p: 3, borderBottom: '1px solid #f2f2f2' }}>
        <Typography variant="h6" fontWeight="bold">
          Set a fraud status for this applicant
        </Typography>
        <Typography variant="body2" color="text.secondary" mt={0.5}>
          {isAwaitingReview && isFraudTeamMember
            ? 'This reference is under investigation by the fraud team. Select a category and leave a comment to update the status.'
            : isAwaitingReview
              ? 'This reference is under investigation by the fraud team.'
              : `This reference is marked as ${toFraudStatusLabel(
                  fraudStatus,
                )}. Leave a comment to send the applicant to the fraud team for review.`}
        </Typography>
        {(fraudReason || fraudDecision) && (
          <Box
            sx={{
              mt: 2,
              p: 2,
              bgcolor: '#fff8e1',
              border: '1px solid #ffe082',
              borderLeft: '4px solid #f9a825',
              borderRadius: '6px',
            }}
          >
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
              Current reason on record
            </Typography>
            {fraudDecision && (
              <Typography variant="body2" fontWeight={700} mb={fraudReason ? 0.75 : 0}>
                {FRAUD_DECISION_LABELS[fraudDecision]}
              </Typography>
            )}
            {fraudReason && (
              <Typography variant="body2" color="text.secondary">
                {fraudReason}
              </Typography>
            )}
          </Box>
        )}
      </Box>

      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          p: 3,
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
        }}
      >
        <SidebarContent
          loading={loading}
          isAwaitingReview={isAwaitingReview}
          isFraudTeamMember={!!isFraudTeamMember}
          decision={decision}
          reason={reason}
          error={error}
          onDecisionChange={setDecision}
          onReasonChange={setReason}
        />
      </Box>

      <Box
        sx={{
          p: 3,
          borderTop: '1px solid #f2f2f2',
          display: 'flex',
          gap: 1.5,
        }}
      >
        <Button variant="outlined" fullWidth onClick={onClose} sx={outlinedButtonSx}>
          Cancel
        </Button>
        {isAwaitingReview && isFraudTeamMember && (
          <Button
            variant="contained"
            fullWidth
            disabled={loading || !canConfirmFraudTeam}
            onClick={handleFraudTeamConfirm}
            sx={containedButtonSx}
          >
            Confirm
          </Button>
        )}
        {!isAwaitingReview && (
          <Button
            variant="contained"
            fullWidth
            disabled={loading || !canSendToFraudTeam}
            onClick={handleSendToFraudTeam}
            sx={containedButtonSx}
          >
            Send to fraud team
          </Button>
        )}
      </Box>
    </Drawer>
  )
}
export default FraudFlagSidebar
